// The dialer API contract (spec 04). Implemented by the fake API (demo/tests) and the Supabase RPC API.
import type {
  LeadSnapshot,
  SessionFilter,
  CallOutcome,
} from "../../domain/dialer/types";
import type { OutcomePayload } from "../../domain/dialer/outcomes";
import type { QueueFilterOption } from "../../domain/dialer/filterOptions";
import type { SalesDialPrefs } from "../provider/selectProvider";
import type { InboundMatch } from "../../domain/dialer/inboundCall"; // GAVION inbound calls

export type DialSession = {
  id: number;
  sales_id: number;
  mode: "A" | "B";
  started_at: string;
  ended_at: string | null;
  paused: boolean;
  dials: number;
  contacts: number;
  demos: number;
  filter: SessionFilter;
  /**
   * GAVION dialer pin (SQL 64_): the lead asked for with « Appeler », served again by claimNext until its result or
   * « Passer ». Absent on rows read before the migration.
   */
  pinned_company_id?: number | null;
};
export type ClaimReason =
  | "ok"
  | "queue_empty"
  | "outside_calling_hours"
  | "session_paused"
  | "suppressed"
  | "locked"
  | "invalid_phone";
/** GAVION contact-call: which contact number a manual dial targets (deep link contact_id + phone). */
export type ContactCallTarget = { contactId: number; phone: string };
/** GAVION contact-call: the contact being called, as shown on the dialer card. */
export type CalledContact = {
  id: number;
  name: string;
  title: string | null;
  /** The dialled number, E.164. */
  phone_e164: string;
  /** The phone_jsonb type of that number (Work, Home, Other), as stored. */
  phone_type: string | null;
};
export type ClaimResult = {
  reason: ClaimReason;
  session_id: number;
  company_id: number | null;
  call_id: number | null;
  lead: LeadSnapshot | null;
  locked_by_name?: string | null;
  next_window?: string | null;
  next_callback_at?: string | null;
  /**
   * Manual dial only: `cooling_down` → the UI asks for confirmation (cooldown never blocks);
   * `assigned_to_other` → allowed (every rep sees every lead), shown as a notice.
   */
  warning?: "cooling_down" | "assigned_to_other" | null;
  /** GAVION contact-call: set when the claim dials a contact's number (claimSpecific with a target). */
  contact?: CalledContact | null;
  /**
   * The number recorded on the claimed call row (calls.to_number): the one the dialer dials (review fix I1/M1).
   * Absent when unknown: the dialer then falls back to the contact's number, else (lead dial only) the lead's.
   */
  to_number?: string | null;
  /**
   * GAVION requeue fix: claimNext handed back the rep's open call on this lead (no new row, no dial): when it was
   * claimed. The card says so and nothing is auto-dialled. Null/absent for a fresh call.
   */
  resumed_at?: string | null;
};
/** GAVION callbacks today: a callback still to come today (« Plus tard aujourd'hui » under « File »). */
export type LaterCallback = { companyId: number; name: string; at: string };
/** GAVION skip-callback: why a rep removes a callback (SQL public.skip_callback, 93_). */
export const SKIP_CALLBACK_REASONS = [
  "inaccurate",
  "already_done",
  "not_relevant",
] as const;
export type SkipCallbackReason = (typeof SKIP_CALLBACK_REASONS)[number];
export type CallRow = {
  id: number;
  company_id: number | null;
  /** GAVION contact-call: the contact called (null = the lead's main number). */
  contact_id?: number | null;
  sales_id: number;
  dial_session_id: number | null;
  direction: "outbound" | "inbound";
  to_number: string | null;
  /** GAVION inbound calls: the caller (E.164) of an inbound call. */
  from_number?: string | null;
  claimed_at: string | null;
  dialed_at: string;
  /** GAVION small fixes: when the dialer launched the softphone (first launch; SQL mark_call_launched). */
  launched_at?: string | null;
  outcome: CallOutcome | null;
  outcome_version: number;
  outcome_at: string | null;
  note: string | null;
  callback_at: string | null;
  /**
   * demo_booked: { at, duration_min, attendee_email, amount }; GAVION demo-contact: + contact_id (the attendee's
   * contact) and contact_linked (contact_id was set by the demo on a main-line call), 68_gavion_demo_contact.sql.
   */
  demo?: Record<string, unknown> | null;
  voipms_cdr_id: string | null;
  deal_id: number | null;
  duration_s: number | null;
  disposition: string | null;
  /** GAVION inbound calls (pre-deploy review minor 7): the CDR's billed seconds; answered = ANSWERED and > 0. */
  billsec_s?: number | null;
  company_name?: string | null;
  /** todoCalls only: the lead's zone and email for the outcome dialog (S1 SQL: join companies in the todo RPC). */
  company_timezone?: string | null;
  company_email?: string | null;
  /** GAVION inbound calls: when the rep hung up (end_inbound_call). */
  ended_at?: string | null;
  /** GAVION inbound calls (start/end_inbound_call): the contact on the call, when there is one. */
  contact_name?: string | null;
  /** GAVION off-CRM call: logged after a call made from a cell (SQL log_off_crm_call, 86_). */
  off_crm?: boolean;
};

/**
 * GAVION off-CRM call (SQL public.log_off_crm_call, 86_): a call made outside the CRM and its result, logged at once.
 * `contact_id` null = the lead's main line; `called_at` within the last 7 days; `call`: log_call_outcome's payload.
 */
export type OffCrmCallInput = {
  company_id: number;
  contact_id: number | null;
  called_at: string;
  call: Omit<OutcomePayload, "call_id" | "version">;
};
/**
 * GAVION inbound calls (SQL public.start_inbound_call, 80_): `created` (a new call: Granola opens), `reused` (a
 * repeated answer event: the same call, no second note), `choose` (the number is on several leads), `unknown` (on
 * none: « Créer un lead » / « Associer à un lead »).
 */
export type InboundStartResult =
  | {
      status: "created" | "reused";
      phone: string;
      /** ended_at set: picked after the hang-up (no Granola note; the result is asked at once). */
      call: CallRow;
      matches?: InboundMatch[];
    }
  | {
      status: "choose" | "unknown";
      phone: string;
      /** review I2: the call is recorded at the answer (answer time kept), without a lead until the pick. */
      call: CallRow;
      matches: InboundMatch[];
    };
/**
 * GAVION inbound calls (SQL public.end_inbound_call): the call just ended, already ended, or none from that number. A
 * call still without a lead carries the matches (the page asks for the lead, then the result).
 */
export type InboundEndResult =
  | {
      status: "ended" | "already_ended";
      phone: string;
      call: CallRow;
      matches?: InboundMatch[];
    }
  | { status: "none"; phone: string };
// GAVION my-calls: « Mes appels » (call history). "none" = no result yet (outcome null or unlogged).
export type CallHistoryOutcome = Exclude<CallOutcome, "unlogged"> | "none";
/** calls.direction (SQL enum public.call_direction). */
export type CallDirection = "outbound" | "inbound";
export type CallHistoryQuery = {
  /** Admin only (like todoCalls): every rep's calls, optionally one rep's (salesId); a rep always gets their own. */
  allReps: boolean;
  salesId?: number | null;
  /** dialed_at in [from, to), ISO instants. */
  from: string;
  to: string;
  /** Empty = every result. */
  outcomes: readonly CallHistoryOutcome[];
  /** Null/absent = both directions (fix round 1: inbound calls are shown, marked « Entrant »). */
  direction?: CallDirection | null;
  /** 1-based. */
  page: number;
  perPage: number;
};
export type CallHistoryRow = {
  id: number;
  company_id: number | null;
  contact_id: number | null;
  sales_id: number;
  direction: CallDirection;
  dialed_at: string;
  outcome: CallOutcome | null;
  note: string | null;
  callback_at: string | null;
  duration_s: number | null;
  company_name: string | null;
  /** Set when the call went to a contact (calls.contact_id). */
  contact_name: string | null;
};
export type CallHistoryPage = { rows: CallHistoryRow[]; total: number };
export type LogOutcomeResult = {
  call: CallRow | null;
  demo_deal_id: number | null;
};
export type ActivityItem = {
  kind: "call" | "note";
  id: number;
  at: string;
  title: string;
  body: string | null;
};
export type TodayStats = { dials: number; contacts: number; demos: number };

export class DialerApiError extends Error {
  readonly code: string;
  readonly detail?: string;
  constructor(code: string, detail?: string) {
    super(code);
    this.name = "DialerApiError";
    this.code = code;
    this.detail = detail;
  }
}

/**
 * The request never got a server answer (offline, DNS, aborted, gateway 502/503/504). A TypeError, like
 * fetch's own failure, so retry logic (outbox, lease renewal) keeps the work instead of dropping it.
 */
export class DialerNetworkError extends TypeError {
  constructor(message: string) {
    super(message);
    this.name = "DialerNetworkError";
  }
}

/**
 * sales.prefs as read by the app: the dial prefs plus the daily digest switch (X4; absent = on) and the « Appel
 * manqué » email switch (GAVION rep-missed-call; absent = off).
 */
export type MyPrefs = SalesDialPrefs & {
  digest_enabled?: boolean;
  missed_call_email?: boolean;
};

/** A null value removes the key (SQL: set_my_dial_prefs). */
export type SalesDialPrefsPatch = {
  [K in keyof SalesDialPrefs]?: SalesDialPrefs[K] | null;
};

export interface DialerApi {
  getActiveSession(): Promise<DialSession | null>;
  startSession(filter: SessionFilter): Promise<DialSession>;
  endSession(sessionId: number): Promise<void>;
  pauseSession(sessionId: number, paused: boolean): Promise<void>;
  /** `exclude`: leads skipped in this session, never handed back (SQL p_exclude). */
  claimNext(
    sessionId: number,
    exclude?: readonly number[],
  ): Promise<ClaimResult>;
  /**
   * Manual dial. With `contact`, dials that contact's number instead of the lead's (SQL claim_contact_call);
   * throws DialerApiError("not_found") when the contact is not the lead's.
   */
  claimSpecific(
    sessionId: number | null,
    companyId: number,
    contact?: ContactCallTarget | null,
  ): Promise<ClaimResult>;
  /** Returns the new locked_until; throws DialerApiError("lease_lost"). */
  renewLease(companyId: number): Promise<string>;
  releaseLease(companyId: number): Promise<void>;
  /**
   * GAVION small fixes: the softphone URI was just opened for this call (SQL mark_call_launched). My own call without
   * an outcome only (else DialerApiError("not_found")); the first launch is kept. Returns the stored launch time.
   */
  markCallLaunched(callId: number): Promise<string>;
  /** Idempotent on (call_id, version). */
  logOutcome(p: OutcomePayload): Promise<LogOutcomeResult>;
  /** GAVION off-CRM call: creates the call and logs its result (one transaction); throws like logOutcome. */
  logOffCrmCall(p: OffCrmCallInput): Promise<CallRow>;
  /**
   * GAVION skip-callback (SQL public.skip_callback, 93_): « Sauter le rappel » clears the lead's next callback (its
   * assigned rep or an admin) and writes a history note. Throws DialerApiError forbidden / invalid_reason /
   * not_found / no_callback.
   */
  skipCallback(companyId: number, reason: SkipCallbackReason): Promise<void>;
  /** `exclude`: this session's skipped leads, left out like claimNext does. */
  queuePreview(
    limit: number,
    exclude?: readonly number[],
  ): Promise<LeadSnapshot[]>;
  /**
   * Start screen filters: every trade and region with how many of my leads are callable right now (queue rules,
   * session filter ignored). SQL: my_queue_filter_options.
   */
  queueFilterOptions(): Promise<QueueFilterOption[]>;
  /** GAVION callbacks today: my callbacks still to come today, earliest first (SQL: my_callbacks_later_today). */
  callbacksLaterToday(): Promise<LaterCallback[]>;
  todayStats(): Promise<TodayStats>;
  todoCalls(opts: {
    allReps: boolean;
    salesId?: number | null;
  }): Promise<CallRow[]>;
  /** GAVION my-calls: one page of calls, newest first, with the lead and contact names (« Mes appels »). */
  callHistory(q: CallHistoryQuery): Promise<CallHistoryPage>;
  recentActivity(companyId: number, limit: number): Promise<ActivityItem[]>;
  deleteDeal(dealId: number): Promise<void>;
  getMyPrefs(): Promise<MyPrefs>;
  /** Merges allowed keys only (SQL: set_my_dial_prefs). */
  saveMyPrefs(patch: SalesDialPrefsPatch): Promise<SalesDialPrefs>;
  /** GAVION X4: the daily digest opt-out (SQL: set_my_digest_enabled); returns the resulting prefs. */
  setMyDigestEnabled(enabled: boolean): Promise<MyPrefs>;
  /**
   * GAVION rep-missed-call: the rep's own « Appel manqué » email switch (SQL: set_my_missed_call_email); returns the
   * resulting prefs.
   */
  setMyMissedCallEmail(enabled: boolean): Promise<MyPrefs>;
  /**
   * GAVION inbound calls: the rep answered a call from `phone` (SQL start_inbound_call). With `companyId` (the rep's
   * pick, optionally a contact of that lead) the call goes there. Throws DialerApiError("invalid_phone" | "not_found"
   * | "forbidden").
   */
  startInboundCall(p: {
    phone: string;
    companyId?: number | null;
    contactId?: number | null;
  }): Promise<InboundStartResult>;
  /** GAVION inbound calls: the rep hung up on `phone` (SQL end_inbound_call). */
  endInboundCall(phone: string): Promise<InboundEndResult>;
}
